import { cloneElement, isValidElement, useEffect, useRef, useState } from "react";
import type { ReactNode, ReactElement } from "react";
import { cn } from "@/lib/cn";

export function nextMenuIndex(index: number, key: string, count: number): number {
  if (!count) return -1;
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  if (key === "ArrowDown") return (index + 1 + count) % count;
  if (key === "ArrowUp") return (index - 1 + count) % count;
  return index;
}

export interface MenuItem {
  label: string;
  onSelect: () => void;
  danger?: boolean;
}

export interface MenuProps {
  trigger: ReactNode;
  items: MenuItem[];
  align?: "start" | "end";
  className?: string;
}

/** Minimal dropdown menu: closes on outside click, Escape, or item select. */
export function Menu({ trigger, items, align = "end", className }: MenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const close = (restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) requestAnimationFrame(() => triggerRef.current?.focus());
  };
  // The ref is attached to the cloned native trigger so focus returns to the actual control.
  const triggerElement = isValidElement(trigger)
    ? // eslint-disable-next-line react-hooks/refs
      cloneElement(trigger as ReactElement<{ onClick?: () => void; "aria-haspopup"?: string; "aria-expanded"?: boolean; ref?: React.Ref<HTMLElement> }>, {
        ref: (node: HTMLElement | null) => { triggerRef.current = node; },
        "aria-haspopup": "menu",
        "aria-expanded": open,
        onClick: () => setOpen((value) => !value),
      })
    : trigger;

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => itemRefs.current[0]?.focus());
    function handlePointerDown(event: PointerEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        close();
      } else if (event.key === "Tab") {
        close(false);
      }
    }
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={ref} className={cn("relative inline-flex", className)}>
      {triggerElement}
      {open && (
        <div
          role="menu"
          className={cn(
            "absolute top-full z-50 mt-2 min-w-40 rounded-sm border border-border bg-surface-1 py-1 shadow-[var(--shadow-popover)]",
            align === "end" ? "right-0" : "left-0",
          )}
        >
          {items.map((item, index) => (
            <button
              key={item.label}
              ref={(element) => { itemRefs.current[index] = element; }}
              type="button"
              role="menuitem"
              onKeyDown={(event) => {
                if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
                  event.preventDefault();
                  const next = nextMenuIndex(items.findIndex((entry) => entry.label === item.label), event.key, items.length);
                  itemRefs.current[next]?.focus();
                } else if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  close();
                  item.onSelect();
                }
              }}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              className={cn(
                "block w-full px-3 py-2 text-left text-small hover:bg-surface-3",
                item.danger ? "text-danger" : "text-text-primary",
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
